import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {getPripremaMaterijala} from '../../../api/radniNalozi'
import notifyToast from '../../../components/Toast/Toast'
import {getAuth} from '../../../modules/auth'

const normalizeResponseData = (response: any) => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.data)) return response.data
    if (Array.isArray(response?.data?.data)) return response.data.data
    return []
}

const getItemId = (item: any) => item?.Id ?? item?.id ?? `${item?.RolnaId ?? ''}-${item?.TrakaId ?? ''}`

const ZaduzeniMaterijal: React.FC = () => {
    const [data, setData] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'moja' | 'sva'>('moja')
    const navigate = useNavigate()
    const auth = getAuth()

    useEffect(() => {
        setLoading(true)
        getPripremaMaterijala()
            .then((response) => setData(normalizeResponseData(response)))
            .catch((error) => notifyToast(false, error.message))
            .finally(() => setLoading(false))
    }, [])

    const mojaZaduzenja = useMemo(() => {
        return data.filter((item) => {
            const itemUposlenikId = item?.uposlenik?.UposlenikId ?? item?.UposlenikId ?? item?.Kreirao
            const itemUposlenikIme = item?.uposlenik?.UposlenikImePrezime

            if (auth?.UposlenikId && itemUposlenikId) {
                return String(itemUposlenikId) === String(auth.UposlenikId)
            }

            return Boolean(auth?.UposlenikImePrezime && itemUposlenikIme === auth.UposlenikImePrezime)
        })
    }, [auth?.UposlenikId, auth?.UposlenikImePrezime, data])

    const displayedData = activeTab === 'moja' ? mojaZaduzenja : data

    const grouped = useMemo(() => {
        const trake = displayedData.filter((item) => Boolean(item?.TrakaId))
        const rolne = displayedData.filter((item) => Boolean(item?.RolnaId) && !item?.TrakaId)
        return {rolne, trake}
    }, [displayedData])

    const openPromet = (item: any) => {
        if (item?.TrakaId) {
            navigate(`/terminali/zaduzeni-materijal/traka/${item.TrakaId}`)
            return
        }

        if (item?.RolnaId) {
            navigate(`/terminali/zaduzeni-materijal/rolna/${item.RolnaId}`)
        }
    }

    if (loading) {
        return (
            <div className='d-flex align-items-center justify-content-center py-10'>
                <span className='spinner-border me-3' role='status' aria-hidden='true' />
                <span className='text-muted fw-semibold'>Učitavanje...</span>
            </div>
        )
    }

    if (!data.length) {
        return (
            <div className='alert alert-light border d-flex align-items-center gap-3'>
                <i className='bi bi-inbox fs-3 text-muted' />
                <div>
                    <div className='fw-bold'>Nema zaduženog materijala</div>
                    <div className='text-muted'>Trenutno nema rolni ili traka za promet.</div>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4'>
                <div>
                    <div className='fw-bold fs-3'>Zaduženi materijal</div>
                    <div className='text-muted'>Odaberi rolnu ili traku za promet.</div>
                </div>

                <div className='d-flex align-items-center gap-2 flex-wrap'>
                    <span className='badge badge-light-primary fw-bold'>
                        <i className='bi bi-arrow-repeat me-2' />
                        Rolne: {grouped.rolne.length}
                    </span>
                    <span className='badge badge-light-danger fw-bold'>
                        <i className='bi bi-scissors me-2' />
                        Trake: {grouped.trake.length}
                    </span>
                    <span className='badge badge-light fw-bold'>Ukupno: {data.length}</span>
                </div>
            </div>

            <div className='d-flex gap-2 flex-wrap mb-4'>
                <button
                    type='button'
                    className={`btn ${activeTab === 'moja' ? 'btn-primary' : 'btn-light-primary'} flex-grow-1 flex-md-grow-0`}
                    onClick={() => setActiveTab('moja')}
                >
                    Moja zaduženja ({mojaZaduzenja.length})
                </button>
                <button
                    type='button'
                    className={`btn ${activeTab === 'sva' ? 'btn-primary' : 'btn-light-primary'} flex-grow-1 flex-md-grow-0`}
                    onClick={() => setActiveTab('sva')}
                >
                    Sva zaduženja ({data.length})
                </button>
            </div>

            {!displayedData.length && (
                <div className='alert alert-light border d-flex align-items-center gap-3'>
                    <i className='bi bi-inbox fs-3 text-muted' />
                    <div>
                        <div className='fw-bold'>Nema zaduzenog materijala</div>
                        <div className='text-muted'>Trenutno nema zapisa za odabrani prikaz.</div>
                    </div>
                </div>
            )}

            <div className='row'>
                {displayedData.map((item) => {
                    const isTraka = Boolean(item?.TrakaId)
                    const materialId = isTraka ? item.TrakaId : item.RolnaId
                    const typeLabel = isTraka ? 'TRAKA' : 'ROLNA'
                    const idLabel = isTraka ? `Traka: ${materialId}` : `Rolna: ${materialId}`
                    const accentText = isTraka ? 'text-danger' : 'text-primary'
                    const accentBorder = isTraka ? 'border-danger' : 'border-primary'
                    const badgeClass = isTraka ? 'badge-light-danger' : 'badge-light-primary'
                    const iconClass = isTraka ? 'bi-scissors' : 'bi-arrow-down-square'

                    return (
                        <div className='col-12 col-md-6 col-xl-4 mt-3' key={getItemId(item)}>
                            <button
                                type='button'
                                className='card card-xl-stretch border shadow-sm bg-white promet-card text-start w-100 h-100'
                                style={{borderLeftWidth: 4, borderLeftStyle: 'solid'}}
                                onClick={() => openPromet(item)}
                            >
                                <div className={`card-body p-5 border-start ${accentBorder}`} style={{borderLeftWidth: 4}}>
                                    <div className='d-flex align-items-start justify-content-between gap-3'>
                                        <div className='d-flex align-items-center gap-3'>
                                            <div
                                                className={`d-flex align-items-center justify-content-center rounded-3 bg-light border ${accentBorder}`}
                                                style={{width: 46, height: 46}}
                                            >
                                                <i className={`bi fs-2 ${iconClass} ${accentText}`} />
                                            </div>

                                            <div>
                                                <div className={`fw-bold fs-4 mb-1 ${accentText}`}>{idLabel}</div>
                                                <div className='text-muted fw-semibold'>
                                                    Uposlenik: {item?.uposlenik?.UposlenikImePrezime ?? '-'}
                                                </div>
                                            </div>
                                        </div>

                                        <span className={`badge fw-bold ${badgeClass}`}>{typeLabel}</span>
                                    </div>

                                    <div className='separator separator-dashed my-4'></div>

                                    <div className='d-flex flex-column gap-2'>
                                        <div className='d-flex justify-content-between gap-3'>
                                            <span className='text-muted'>Stavka radnog naloga</span>
                                            <span className='fw-semibold text-end'>
                                                {item?.IdStavkeRadnogNaloga ?? item?.IdStavkeFMK ?? item?.Stavka ?? '-'}
                                            </span>
                                        </div>
                                        <div className='d-flex justify-content-between gap-3'>
                                            <span className='text-muted'>Potrebna količina (normativ)</span>
                                            <span className='fw-semibold text-end'>{item?.PotrebnaKolicina ?? '-'}</span>
                                        </div>
                                        <div className='d-flex justify-content-between gap-3'>
                                            <span className='text-muted'>Skenirano</span>
                                            <span className={`badge ${Number(item?.Skenirano ?? item?.Skrenirano) === 1 ? 'bg-success' : 'bg-danger'}`}>
                                                {Number(item?.Skenirano ?? item?.Skrenirano) === 1 ? 'DA' : 'NE'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className='mt-4 d-flex align-items-center justify-content-between'>
                                        <span className='text-muted d-flex align-items-center gap-2'>
                                            <i className='bi bi-clock' />
                                            Spremno za promet
                                        </span>
                                        <span className={`btn btn-sm btn-icon ${isTraka ? 'btn-light-danger' : 'btn-light-primary'}`}>
                                            <i className='bi bi-arrow-right' />
                                        </span>
                                    </div>
                                </div>
                            </button>
                        </div>
                    )
                })}
            </div>

            <style>
                {`
                    .promet-card {
                        transition: transform .15s ease, box-shadow .15s ease;
                    }
                    .promet-card:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 24px rgba(0,0,0,.08) !important;
                    }
                `}
            </style>
        </>
    )
}

export default ZaduzeniMaterijal
